import React, { useState, useEffect, useMemo } from 'react';
import styles from './styles/StepRfqPreference.module.css';
import sharedStyles from './styles/BrandSignup.module.css';
import { Luna } from './Luna';

const StepRfqPreference = ({ brandSignup, onComplete, onSkipToDashboard, loading, error, onBack, canGoBack }) => {
  const [autoFillRfq, setAutoFillRfq] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    // Pre-fill if we have step data
    const stepData = brandSignup.step_data?.['rfq_preference'];
    if (stepData) {
      setAutoFillRfq(stepData.auto_fill_rfq || '');
    }
  }, [brandSignup.step_data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    localStorage.setItem('showAutofill', autoFillRfq === 'yes');

    if (!autoFillRfq) {
      return;
    }

    onComplete({
      auto_fill_rfq: autoFillRfq
    });
  };

  const handleChange = (value) => {
    setAutoFillRfq(value);
    if (touched && value) {
      setTouched(false);
    }
  };

  // Memoize Luna messages and component to prevent re-typing on form changes
  const lunaMessages = useMemo(() => [
    "We're ready to build your RFQ (Request For Quotation)! Would you like me to auto-generate some of the details for you to review or would you like to do it yourself?"
  ], []);

  const memoizedLuna = useMemo(() =>
    <Luna lunaDialogs={lunaMessages} />,
    [lunaMessages]
  );

  return (
    <div className={styles.stepContainer}>
      {memoizedLuna}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.radioGroup}>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="autoFillRfq"
              value="yes"
              checked={autoFillRfq === 'yes'}
              onChange={(e) => handleChange(e.target.value)}
              className={styles.radioInput}
              disabled={loading}
            />
            <div className={styles.radioContent}>
              <div className={styles.radioTitle}>
                <span className={styles.radioCircle}></span>
                <span>✨ Yes, help me get started</span>
              </div>
              <p className={styles.radioDescription}>
                I'll use AI to analyze your project and suggest materials, capabilities,
                and other details to save you time. You can review and edit everything afterwards.
              </p>
            </div>
          </label>

          <label className={styles.radioOption}>
            <input
              type="radio"
              name="autoFillRfq"
              value="no"
              checked={autoFillRfq === 'no'}
              onChange={(e) => handleChange(e.target.value)}
              className={styles.radioInput}
              disabled={loading}
            />
            <div className={styles.radioContent}>
              <div className={styles.radioTitle}>
                <span className={styles.radioCircle}></span>
                <span>✏️ No, I'll do everything manually</span>
              </div>
              <p className={styles.radioDescription}>
                Start with a blank RFQ and add all details yourself.
                You'll have full control over every aspect of your request.
              </p>
            </div>
          </label>
        </div>

        {touched && !autoFillRfq && (
          <p className={styles.errorMessage}>Please select an option to continue</p>
        )}

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        <button
          className={styles.button}
          type="submit"
          disabled={loading || !autoFillRfq}
        >
          {loading ? 'Processing...' :
           autoFillRfq === 'yes' ? 'Let\'s build your RFQ!' : 'Continue'}
        </button>

        {canGoBack && (
          <a
            className={sharedStyles.backLink}
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back
          </a>
        )}

        {onSkipToDashboard && (
          <button
            type="button"
            className={sharedStyles.skipLink}
            onClick={onSkipToDashboard}
            disabled={loading}
          >
            I'll set up my project later. Take me to the Dashboard
          </button>
        )}
      </form>
    </div>
  );
};

export default StepRfqPreference;
